import { Box, Button, Grid, Stack, TextField, Typography } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, type ModalProps } from '@shared/ui';

export type ConfirmDeleteByNameModalProps = {
  /** Whether the modal is visible. */
  open: boolean;
  /** Dialog title, e.g. "Delete company". */
  title: string;
  /** Bold heading above the description, e.g. "Confirm deletion". */
  heading: string;
  /** Explanatory copy describing the consequence of the deletion. */
  description: string;
  /**
   * The exact value the user must re-type to enable the delete button
   * (e.g. the company / audit name). The comparison is case-sensitive.
   */
  confirmName?: string;
  /** Placeholder for the confirmation input. */
  inputPlaceholder?: string;
  /** Shows the spinner on the delete button and keeps it busy. */
  isPending?: boolean;
  /** Overrides the default global "delete" label. */
  submitLabel?: string;
  /** `data-testid` applied to the confirmation input. */
  inputTestId?: string;
  /** `data-testid` applied to the delete button. */
  submitTestId?: string;
  onClose: () => void;
  onSubmit: () => void;
} & Partial<Pick<ModalProps, 'maxWidth' | 'className' | 'data-testid'>>;

/**
 * Destructive-confirmation modal that gates deletion behind re-typing the
 * item's name. Domain dialogs (delete company, delete audit, …) configure it
 * with their own copy and test ids rather than re-implementing the flow.
 */
export default function ConfirmDeleteByNameModal({
  open,
  title,
  heading,
  description,
  confirmName,
  inputPlaceholder,
  isPending = false,
  submitLabel,
  inputTestId,
  submitTestId,
  onClose,
  onSubmit,
  ...modalProps
}: ConfirmDeleteByNameModalProps) {
  const { t: tGlobal } = useTranslation('dashboard', { keyPrefix: 'global' });
  const [value, setValue] = useState('');

  const handleClose = () => {
    setValue('');
    onClose();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value);

  return (
    <Modal open={open} onClose={handleClose} maxWidth='sm' {...modalProps}>
      <Modal.Header title={title} onClose={handleClose} />
      <Modal.Body>
        <Grid container spacing={3}>
          <Grid size={4}>
            <Box>
              <Typography variant='body2' gutterBottom fontWeight={600}>
                {heading}
              </Typography>
              <Typography variant='caption' color='text.secondary' display='block' fontWeight={500}>
                {description}
              </Typography>
            </Box>
          </Grid>
          <Grid size={8}>
            <TextField
              autoFocus
              fullWidth
              placeholder={inputPlaceholder}
              value={value}
              onChange={handleChange}
              variant='outlined'
              slotProps={{ htmlInput: { 'data-testid': inputTestId } }}
            />
          </Grid>
        </Grid>
      </Modal.Body>
      <Modal.Footer>
        <Stack direction='row' spacing={2} justifyContent='flex-end'>
          <Button variant='outlined' color='error' onClick={handleClose}>
            {tGlobal('cancel')}
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={onSubmit}
            loading={isPending}
            disabled={value !== confirmName}
            data-testid={submitTestId}
          >
            {submitLabel ?? tGlobal('delete')}
          </Button>
        </Stack>
      </Modal.Footer>
    </Modal>
  );
}
